import jwt from "jsonwebtoken";
import { flatten, uniq } from "lodash";
import { getParent, Instance, types } from "mobx-state-tree";
import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import { config } from "@fidget/common/config/config";
import { staticConfig } from "@fidget/common/config/static-config";
import { agentGroupsStoreGetter, currentUserStoreGetter } from "../utils";
import { ReportsScope } from "./ReportsScope";

export const Report = types
  .model("Report", {
    id: types.identifier,
    url: types.maybe(types.string),
  })
  .actions((self) => ({
    setUrl(url: string) {
      self.url = url;
    }
  }))
  .views(currentUserStoreGetter)
  .views(agentGroupsStoreGetter)
  .views((self) => ({
    get scope(): ReportsScope {
      return (getParent(getParent(self)) as any).scope;
    }
  }))
  .actions((self) => ({
    createUrl() {
      const getScopeParams = (): { userids: string } => {
        switch (self.scope) {
          case ReportsScope.Yours:
            return { userids: self.currentUserStore.profile!.id };
          case ReportsScope.Groups:
            const ids = uniq(
              flatten(
                self.agentGroupsStore
                  .getByIds(self.currentUserStore.profile!.groups)
                  .map((ag) => ag.adviserIds.slice())
              )
            );
            return {
              userids: ids.join(",")
            };
          case ReportsScope.Conversations:
          case ReportsScope.Advisers:
          case ReportsScope.Org:
            return { userids: staticConfig.metabaseUserIdsAllQuery };
          default:
            assertUnreachable(self.scope);
            throw Error("Invalid scope: " + self.scope);
        }
      };

      const metabaseJwt = config.services.metabase.jwt;
      const jwtExpiration = config.services.metabase.jwtExpirationInMinutes;
      const baseUrl = config.services.metabase.baseUrlDashboards;
      const scopeParams = getScopeParams();
      const { orgId } = self.currentUserStore;
      const params = { orgid: orgId, agent_group_orgid: orgId, ...scopeParams }; // metabase needs lowercase
      const resource = { dashboard: parseInt(self.id) };
      const refresh = config.services.metabase.autoRefreshInterval !== undefined
        ? "&refresh=" + config.services.metabase.autoRefreshInterval
        : "";

      const payload = {
        resource,
        params,
        exp: Math.round(Date.now() / 1000) + jwtExpiration * 60
      };

      const token = jwt.sign(payload, metabaseJwt);
      const url = baseUrl + token + "#bordered=false&titled=false" + refresh;
      self.setUrl(url);
    }
  }))
  .views((self) => ({
    get metabaseUrl() {
      return self.url;
    }
  }))
  .actions((self) => ({
    afterCreate() {
      self.createUrl();
    }
  }));

export interface IReport extends Instance<typeof Report> {}
